import React, {useState} from 'react';
import {
  Box,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import {TextG14} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import {reportUser} from '../../../endpoints/api';
import {useBlockUser} from '../../../hooks/useBlockUser';
import styled from 'styled-components';

const SubmitBtn = styled.button<{$disabled: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#E02F84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

interface ReportBlockMenuProps {
  userId: number;
  userName: string;
  isOpen: boolean;
  onClose: () => void;
}

const ReportBlockMenu = ({userId, userName, isOpen, onClose}: ReportBlockMenuProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const blockUserMutation = useBlockUser(userId);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReport = async () => {
    if (!reportReason.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await reportUser(userId, reportReason.trim());
      toast({title: 'Report submitted. Thank you.', status: 'success', duration: 3000});
      setShowReportModal(false);
      setReportReason('');
    } catch {
      toast({title: 'Could not submit report', status: 'error', duration: 3000});
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlock = async () => {
    onClose();
    try {
      await blockUserMutation.mutateAsync(userId);
      toast({title: `${userName} has been blocked`, status: 'info', duration: 3000});
      navigate(-1);
    } catch {
      toast({title: 'Could not block user', status: 'error', duration: 3000});
    }
  };

  return (
    <>
      {/* Backdrop to close menu on outside tap */}
      {isOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={9}
          onClick={onClose}
        />
      )}

      {/* Dropdown menu */}
      {isOpen && (
        <Box
          position="absolute"
          right="16px"
          top="44px"
          zIndex={10}
          bg="white"
          minW="180px"
          boxShadow="0 4px 16px rgba(0,0,0,0.12)"
          borderRadius="12px"
          py={2}
          overflow="hidden">
          <Box
            as="button"
            w="100%"
            textAlign="left"
            fontFamily="Gordita"
            fontSize="14px"
            py={3}
            px={4}
            bg="transparent"
            border="none"
            cursor="pointer"
            _hover={{bg: '#F5F5F5'}}
            onClick={() => {
              onClose();
              setShowReportModal(true);
            }}>
            Report user
          </Box>
          <Box
            as="button"
            w="100%"
            textAlign="left"
            fontFamily="Gordita"
            fontSize="14px"
            fontWeight="600"
            color="#E02F84"
            py={3}
            px={4}
            bg="transparent"
            border="none"
            cursor="pointer"
            _hover={{bg: '#F5F5F5'}}
            onClick={handleBlock}>
            Block user
          </Box>
        </Box>
      )}

      <Modal
        show={showReportModal}
        parentCallback={() => setShowReportModal(false)}
        closeButton
        closeButtonPosition="right"
        header={
          <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
            Report {userName}
          </TextG14>
        }
        modalPadding="16px 20px 24px">
        <VStack spacing={4} align="stretch">
          <TextG14 mt="0" color="#636363">
            Please describe why you are reporting this user.
          </TextG14>
          <Textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Reason for reporting..."
            fontFamily="Gordita"
            fontSize="14px"
            borderRadius="12px"
            rows={4}
            resize="none"
          />
          <SubmitBtn
            $disabled={!reportReason.trim() || isSubmitting}
            onClick={handleReport}>
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </SubmitBtn>
        </VStack>
      </Modal>
    </>
  );
};

export default ReportBlockMenu;
